import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { BookOpen, Truck, ArrowLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const HardcoverOrder = ({ autobiographyId, onBack }) => {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    quantity: 1,
    name: '',
    address: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
    phone: '',
    specialInstructions: ''
  });

  const pricePerBook = 45;
  const shippingCost = 5;
  const totalCost = (formData.quantity * pricePerBook) + shippingCost;

  const handleChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    // MOCK: Simulate order placement
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/hardcover/order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          autobiography_id: autobiographyId,
          quantity: formData.quantity,
          name: formData.name,
          address: formData.address,
          city: formData.city,
          postcode: formData.postcode,
          country: formData.country,
          phone: formData.phone,
          special_instructions: formData.specialInstructions
        })
      });
      
      if (!response.ok) {
        throw new Error('Order failed');
      }
      
      const data = await response.json();
      
      toast({
        title: "Order Placed!",
        description: data.message,
      });
    } catch (error) {
      console.error('Order error:', error);
      toast({
        title: "Order Failed",
        description: "Unable to place order. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-6 flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Autobiography
        </Button>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Order Form */}
          <div className="bg-white rounded-3xl shadow-xl p-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-purple-400" />
              Order Hardcover
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Books
                </label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.quantity}
                  onChange={(e) => handleChange('quantity', parseInt(e.target.value) || 1)}
                  className="input-focus"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Perfect for gifting to multiple family members
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <Input
                  type="text"
                  placeholder="John Smith"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  className="input-focus"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delivery Address
                </label>
                <Input
                  type="text"
                  placeholder="123 Main Street"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  required
                  className="input-focus"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <Input
                    type="text"
                    placeholder="London"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    required
                    className="input-focus"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postcode
                  </label>
                  <Input
                    type="text"
                    placeholder="SW1A 1AA"
                    value={formData.postcode}
                    onChange={(e) => handleChange('postcode', e.target.value)}
                    required
                    className="input-focus"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <Input
                  type="tel"
                  placeholder="+44 20 1234 5678"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  required
                  className="input-focus"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Special Instructions (Optional)
                </label>
                <Textarea
                  placeholder="Any special delivery instructions..."
                  value={formData.specialInstructions}
                  onChange={(e) => handleChange('specialInstructions', e.target.value)}
                  className="input-focus"
                  rows={3}
                />
              </div>

              <Button
                type="submit"
                className="btn-primary w-full text-white py-6 text-lg font-semibold rounded-full border-0"
              >
                Place Order - £{totalCost}
              </Button>

              <p className="text-xs text-center text-gray-500">
                This is a MOCK order. No real books will be shipped.
              </p>
            </form>
          </div>

          {/* Order Summary */}
          <div className="space-y-6">
            <div className="bg-white rounded-3xl shadow-xl p-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-6">
                Order Summary
              </h3>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-gray-700">
                  <span>Hardcover Books ({formData.quantity})</span>
                  <span>£{formData.quantity * pricePerBook}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Shipping</span>
                  <span>£{shippingCost}</span>
                </div>
                <div className="border-t pt-4 flex justify-between font-bold text-lg text-gray-800">
                  <span>Total</span>
                  <span>£{totalCost}</span>
                </div>
              </div>

              <div className="bg-purple-50 rounded-xl p-4 space-y-2 text-sm text-gray-700">
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-purple-600" />
                  <span className="font-medium">Estimated Delivery: 7-10 business days</span>
                </div>
                <ul className="ml-6 space-y-1 text-gray-600">
                  <li>• Premium hardcover binding</li>
                  <li>• High-quality paper</li>
                  <li>• Professional dust jacket</li>
                  <li>• Gift-ready packaging</li>
                </ul>
              </div>
            </div>

            {/* Book Preview */}
            <div className="bg-white rounded-3xl shadow-xl p-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                What You'll Receive
              </h3>
              <div className="space-y-3 text-sm text-gray-600">
                <p>📖 <strong>Premium Hardcover:</strong> Durable binding with elegant design</p>
                <p>📄 <strong>Quality Paper:</strong> Cream-colored, acid-free pages</p>
                <p>🎨 <strong>Professional Cover:</strong> Custom title and author name</p>
                <p>🎁 <strong>Gift Box:</strong> Beautiful packaging perfect for gifting</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HardcoverOrder;
